'use client';

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setThemeColors } from '../../redux/slices/themeSlice';

import config from '../../config/config';

export default function ThemeProvider({ children }) {
  const dispatch = useDispatch();
  const themeColors = useSelector((state) => state.theme.colors);



  // Fetch theme colors from API
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/theme`);
        const data = await response.json();
        if (data.success && data.data.colors) {
          dispatch(setThemeColors(data.data.colors));
        }
      } catch (error) {

      }
    };
    fetchTheme();
  }, [dispatch]);

  // Apply theme colors as CSS variables (only for public pages, not dashboard)
  useEffect(() => {
    // Only apply website theme colors if NOT in dashboard
    const isDashboard = typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/dashboard');

    if (isDashboard) {
      // For dashboard, don't apply website theme
      return;
    }

    const root = document.documentElement;

    // Helper function to convert hex to HSL
    const hexToHSL = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return '0 0% 0%';

      let r = parseInt(result[1], 16) / 255;
      let g = parseInt(result[2], 16) / 255;
      let b = parseInt(result[3], 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }

      h = Math.round(h * 360);
      s = Math.round(s * 100);
      l = Math.round(l * 100);

      return `${h} ${s}% ${l}%`;
    };

    // Main brand colors - Professional Modern Palette
    root.style.setProperty('--color-primary', themeColors.primary || '#2563eb');
    root.style.setProperty('--color-secondary', themeColors.secondary || '#f59e0b');
    root.style.setProperty('--color-accent', themeColors.accent || '#10b981');

    // Set HSL values for Shadcn UI
    if (themeColors.primary) root.style.setProperty('--primary', hexToHSL(themeColors.primary));
    if (themeColors.secondary) root.style.setProperty('--secondary', hexToHSL(themeColors.secondary));
    if (themeColors.accent) root.style.setProperty('--accent', hexToHSL(themeColors.accent));

    // Comprehensive color application
    if (themeColors.primaryForeground) {
      root.style.setProperty('--color-primary-foreground', themeColors.primaryForeground);
      root.style.setProperty('--primary-foreground', hexToHSL(themeColors.primaryForeground));
    }
    if (themeColors.secondaryForeground) {
      root.style.setProperty('--color-secondary-foreground', themeColors.secondaryForeground);
      root.style.setProperty('--secondary-foreground', hexToHSL(themeColors.secondaryForeground));
    }
    if (themeColors.accentForeground) {
      root.style.setProperty('--color-accent-foreground', themeColors.accentForeground);
      root.style.setProperty('--accent-foreground', hexToHSL(themeColors.accentForeground));
    }

    if (themeColors.background) {
      root.style.setProperty('--color-background', themeColors.background);
      root.style.setProperty('--background', hexToHSL(themeColors.background));
    }
    if (themeColors.foreground) {
      root.style.setProperty('--color-foreground', themeColors.foreground);
      root.style.setProperty('--foreground', hexToHSL(themeColors.foreground));
    }

    if (themeColors.card) {
      root.style.setProperty('--color-card', themeColors.card);
      root.style.setProperty('--card', hexToHSL(themeColors.card));
    }
    if (themeColors.cardForeground) {
      root.style.setProperty('--color-card-foreground', themeColors.cardForeground);
      root.style.setProperty('--card-foreground', hexToHSL(themeColors.cardForeground));
    }

    if (themeColors.muted) {
      root.style.setProperty('--color-muted', themeColors.muted);
      root.style.setProperty('--muted', hexToHSL(themeColors.muted));
    }
    if (themeColors.mutedForeground) {
      root.style.setProperty('--color-muted-foreground', themeColors.mutedForeground);
      root.style.setProperty('--muted-foreground', hexToHSL(themeColors.mutedForeground));
    }

    if (themeColors.border) {
      root.style.setProperty('--color-border', themeColors.border);
      root.style.setProperty('--border', hexToHSL(themeColors.border));
    }
    if (themeColors.input) {
      root.style.setProperty('--color-input', themeColors.input);
      root.style.setProperty('--input', hexToHSL(themeColors.input));
    }
    if (themeColors.ring) {
      root.style.setProperty('--color-ring', themeColors.ring);
      root.style.setProperty('--ring', hexToHSL(themeColors.ring));
    }

    if (themeColors.success) {
      root.style.setProperty('--color-success', themeColors.success);
      root.style.setProperty('--success', hexToHSL(themeColors.success));
    }
    if (themeColors.successForeground) {
      root.style.setProperty('--color-success-foreground', themeColors.successForeground);
      root.style.setProperty('--success-foreground', hexToHSL(themeColors.successForeground));
    }

    if (themeColors.destructive) {
      root.style.setProperty('--color-destructive', themeColors.destructive);
      root.style.setProperty('--destructive', hexToHSL(themeColors.destructive));
    }
    if (themeColors.destructiveForeground) {
      root.style.setProperty('--color-destructive-foreground', themeColors.destructiveForeground);
      root.style.setProperty('--destructive-foreground', hexToHSL(themeColors.destructiveForeground));
    }

    if (themeColors.warning) root.style.setProperty('--color-warning', themeColors.warning);
    if (themeColors.warningForeground) root.style.setProperty('--color-warning-foreground', themeColors.warningForeground);

    if (themeColors.info) root.style.setProperty('--color-info', themeColors.info);
    if (themeColors.infoForeground) root.style.setProperty('--color-info-foreground', themeColors.infoForeground);

    if (themeColors.popover) {
      root.style.setProperty('--color-popover', themeColors.popover);
      root.style.setProperty('--popover', hexToHSL(themeColors.popover));
    }
    if (themeColors.popoverForeground) {
      root.style.setProperty('--color-popover-foreground', themeColors.popoverForeground);
      root.style.setProperty('--popover-foreground', hexToHSL(themeColors.popoverForeground));
    }

    // Typography colors
    if (themeColors.heading) root.style.setProperty('--color-heading', themeColors.heading);
    if (themeColors.paragraph) root.style.setProperty('--color-paragraph', themeColors.paragraph);
    if (themeColors.link) root.style.setProperty('--color-link', themeColors.link);
    if (themeColors.linkHover) root.style.setProperty('--color-link-hover', themeColors.linkHover);
    if (themeColors.subtitle) root.style.setProperty('--color-subtitle', themeColors.subtitle);
  }, [themeColors]);

  return <>{children}</>;
}
